import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { PrimaryButtonLink, SecondaryButtonLink } from 'components/v2/buttons';
import { CardsEmpty } from 'pages/dashboard/styles';
import { EpdLinks } from 'routes/EpdRoutes';

interface NoOrganisationCardProps {
  className?: string;
  icon: React.ReactNode;
  title: string;
  descriptionLines: string[];
}

/**
 * Empty state shown wherever a user without an organisation lands (dashboard, organisations page).
 * The two actions are fixed by design - only the icon and the copy differ per page.
 */
const NoOrganisationCard: React.FunctionComponent<NoOrganisationCardProps> = ({
  className,
  icon,
  title,
  descriptionLines,
}) => {
  const { t } = useTranslation();

  return (
    <Card className={className}>
      <IconArea>{icon}</IconArea>
      <TextBlock>
        <Title>{title}</Title>
        <Description>
          {descriptionLines.map((line, index) => (
            <React.Fragment key={line}>
              {index > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </Description>
      </TextBlock>
      <ButtonsRow>
        <SecondaryAction to={EpdLinks.organisation('create')}>
          {t('epdDashboard.userHasNoCompanies.createOrg')}
        </SecondaryAction>
        <PrimaryAction to={EpdLinks.organisation('connect')}>
          {t('epdDashboard.userHasNoCompanies.connectToOrg')}
        </PrimaryAction>
      </ButtonsRow>
    </Card>
  );
};

const Card = styled(CardsEmpty)`
  flex-direction: column;
  justify-content: space-between;
  padding: 20px 0 70px;
`;

const IconArea = styled.div`
  svg {
    width: 220px;
    height: 220px;
  }
`;

const TextBlock = styled.div`
  margin-bottom: 50px;
`;

const Title = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 32px;
  line-height: 100%;
  text-align: center;
  color: ${(props) => props.theme.colors.primaryBlack};
`;

const Description = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  margin-top: 20px;
  color: ${(props) => props.theme.colors.primaryBlack70};
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 16px;
`;

const actionSize = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 230px;
  height: 48px;
  padding: 0 2rem;
`;

const PrimaryAction = styled(PrimaryButtonLink)`
  ${actionSize}
`;

const SecondaryAction = styled(SecondaryButtonLink)`
  ${actionSize}
`;

export default NoOrganisationCard;
